import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { useT } from "@/i18n";
import { optionLabels } from "@/hooks/useFilterSet";

/**
 * One filter, many values: a checkbox list in a popover.
 *
 * Built once here rather than per screen, and driven by the same `FilterDef` the URL and the
 * request are driven by, so the control, the chip and the API query cannot describe different
 * filters. A row of native multi-selects was the alternative and is unusable — a `<select
 * multiple>` needs ctrl-click to add a second value and shows two of eight options at a time.
 *
 * On Radix's `DropdownMenu` deliberately: it is already a dependency (the profile menu and
 * the colour picker use it), it portals — the panel sits inside a bordered card that would
 * clip an in-flow popover — and it brings type-ahead, arrow keys and Escape for free. No
 * popover package was added for this.
 *
 * Three details are load-bearing:
 *
 * - **Choosing a value does not close the menu.** Selecting three phases is one gesture; a
 *   menu that shut after each would make multi-select slower than the single select it
 *   replaces. That is what `onSelect`'s `preventDefault` is for.
 * - **The written order is the option order, never the click order.** Two readers who select
 *   the same two phases must produce the same URL, or "the same view is the same link" stops
 *   being true and two identical views miss each other's query cache.
 * - **The accessible name carries the value**, as the colour picker's does: a button named
 *   only "Phase" hides what it is set to from exactly the reader who cannot see the summary.
 */
export function MultiSelectFilter({
  id,
  label,
  options,
  selected,
  onChange,
}: {
  id?: string;
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}): React.JSX.Element {
  const t = useT();

  const chosen = new Set(selected);
  const summary =
    selected.length === 0
      ? t("common.all")
      : selected.length > 2
        ? // Beyond two, the joined labels are wider than the control and truncate to
          // nothing legible. The chip above still names every one of them.
          t("filter.selectedCount", selected.length)
        : optionLabels(options, selected).join(", ");

  const toggle = (value: string, checked: boolean): void => {
    const next = new Set(chosen);
    if (checked) next.add(value);
    else next.delete(value);
    onChange(options.filter((option) => next.has(option.value)).map((option) => option.value));
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          id={id}
          // Names the filter *and* its value, so the value is not sighted-only. The visible
          // field label above is not repeated in the button's text for that reason.
          aria-label={t("filter.valueChip", label, summary)}
          className="border-input bg-background hover:bg-accent focus-visible:ring-ring flex h-9 w-full items-center gap-2 rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none"
        >
          <span className={selected.length === 0 ? "text-muted-foreground truncate" : "truncate"}>{summary}</span>
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-60" aria-hidden />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          // z-50: a Radix popover, which must clear the app header (z-40) — see the
          // z-index ladder in AppLayout's header — wherever this filter opens near the top
          // of a scrolled page.
          className="bg-popover text-popover-foreground z-50 max-h-72 min-w-56 overflow-y-auto rounded-md border p-1 shadow-md"
        >
          {options.length === 0 && (
            // Reference data that has not arrived, or a list with nothing in it. Said, rather
            // than an empty popover that reads as broken.
            <DropdownMenu.Label className="text-muted-foreground px-2 py-1.5 text-xs">
              {t("filter.noOptions")}
            </DropdownMenu.Label>
          )}

          {options.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.value}
              checked={chosen.has(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked === true)}
              // See the class comment: selecting is not finishing.
              onSelect={(event) => event.preventDefault()}
              className="hover:bg-accent data-[highlighted]:bg-accent data-[state=checked]:font-medium flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                <DropdownMenu.ItemIndicator>
                  <Check className="h-4 w-4" aria-hidden />
                </DropdownMenu.ItemIndicator>
              </span>
              {option.label}
            </DropdownMenu.CheckboxItem>
          ))}

          {selected.length > 0 && (
            <>
              <DropdownMenu.Separator className="bg-border my-1 h-px" />
              <DropdownMenu.Item
                onSelect={() => onChange([])}
                className="hover:bg-accent data-[highlighted]:bg-accent text-muted-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
              >
                <span className="h-4 w-4 shrink-0" aria-hidden />
                {t("filter.clearSelection")}
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
